import { useEffect, useState } from "react";
import { Button } from "@/modern/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { LinkCompanyCamHeaderButton } from "@/react/components/entityViews/files/components/LinkCompanyCamHeaderButton";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { useDebounce } from "@/react/hooks/useDebounce";
import { cn } from "@/modern/utils";
import {
  SURFACE_CONTROL_CLS,
  SURFACE_ICON_BTN_CLS,
  SURFACE_SEARCH_CLS,
} from "@/react/components/entityViews/files/constants/surfaceChrome";
import {
  Check,
  Grid3x3,
  LinkIcon,
  List,
  Loader2,
  Search,
  Upload,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { FiltersPopover } from "./FiltersPopover";
import { SortPopover } from "./SortPopover";
import {
  DEFAULT_FILE_BROWSER_FILTERS,
  type FileBrowserFilterState,
} from "./types";

type ViewMode = "grid" | "list";

interface FileGridToolbarProps {
  filters: FileBrowserFilterState;
  onFiltersChange: (next: FileBrowserFilterState) => void;
  /** Commit debounced search into provider filters (avoids stale closures). */
  onSearchChange: (search: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoomLevel: number;
  zoomMax: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  isUploading: boolean;
  onUpload: () => void;
  uploadAcceptLabel: string;
  uploadRef: React.RefObject<HTMLInputElement>;
  accept: string;
  onUploadChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function FileGridToolbar({
  filters,
  onFiltersChange,
  onSearchChange,
  viewMode,
  onViewModeChange,
  zoomLevel,
  zoomMax,
  onZoomIn,
  onZoomOut,
  isUploading,
  onUpload,
  uploadAcceptLabel,
  uploadRef,
  accept,
  onUploadChange,
}: FileGridToolbarProps) {
  const { totalCount, entityRecord, currentCCProject, isImportingCompanyCam } =
    useEntityFileContext();
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debouncedSearch = useDebounce(searchDraft, 300) as string;

  const companyCamProjectId = entityRecord.companyCamProject as
    | number
    | string
    | null
    | undefined;
  const companyCamName =
    currentCCProject?.name ||
    (companyCamProjectId ? `Project ${companyCamProjectId}` : null);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (debouncedSearch === filters.search) return;
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, filters.search, onSearchChange]);

  const patch = (partial: Partial<FileBrowserFilterState>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const resetMenuFilters = () => {
    patch({
      fileTypeTab: DEFAULT_FILE_BROWSER_FILTERS.fileTypeTab,
      source: DEFAULT_FILE_BROWSER_FILTERS.source,
      datePreset: DEFAULT_FILE_BROWSER_FILTERS.datePreset,
    });
  };

  return (
    <div className="my-4 flex shrink-0 flex-col gap-2">
      <div className="flex flex-nowrap items-center gap-2.5">
        {/* Left — search + filters + sort */}
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <div className="relative min-w-0 max-w-md flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60"
              aria-hidden
            />
            <input
              type="text"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Search description or filename…"
              aria-label="Search description or filename"
              className={SURFACE_SEARCH_CLS}
            />
            {searchDraft ? (
              <button
                type="button"
                onClick={() => {
                  setSearchDraft("");
                  onSearchChange("");
                }}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            ) : null}
          </div>

          <FiltersPopover
            filters={filters}
            onChange={patch}
            onResetFilters={resetMenuFilters}
          />
          <SortPopover ordering={filters.ordering} onChange={patch} />
        </div>

        {/* Right — zoom · view · CC · upload */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {viewMode === "grid" ? (
            <div className="inline-flex items-center gap-1" role="group" aria-label="Zoom">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <button
                      type="button"
                      onClick={onZoomOut}
                      disabled={zoomLevel === 0}
                      aria-label="Zoom out"
                      className={cn(
                        SURFACE_ICON_BTN_CLS,
                        "inline-flex items-center justify-center disabled:opacity-40",
                      )}
                    >
                      <ZoomOut className="size-3.5" aria-hidden />
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">Zoom out</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <button
                      type="button"
                      onClick={onZoomIn}
                      disabled={zoomLevel === zoomMax}
                      aria-label="Zoom in"
                      className={cn(
                        SURFACE_ICON_BTN_CLS,
                        "inline-flex items-center justify-center disabled:opacity-40",
                      )}
                    >
                      <ZoomIn className="size-3.5" aria-hidden />
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">Zoom in</TooltipContent>
              </Tooltip>
            </div>
          ) : null}

          <div
            role="group"
            aria-label="View mode"
            className={cn(
              "inline-flex items-center gap-0.5 rounded-full p-0.5",
              SURFACE_CONTROL_CLS,
            )}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onViewModeChange("list")}
                  aria-pressed={viewMode === "list"}
                  aria-label="List view"
                  className={cn(
                    "inline-flex size-8 items-center justify-center rounded-full transition-[transform,background-color] duration-150 ease-out active:scale-[0.97]",
                    viewMode === "list"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <List className="size-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">List view</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onViewModeChange("grid")}
                  aria-pressed={viewMode === "grid"}
                  aria-label="Grid view"
                  className={cn(
                    "inline-flex size-8 items-center justify-center rounded-full transition-[transform,background-color] duration-150 ease-out active:scale-[0.97]",
                    viewMode === "grid"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Grid3x3 className="size-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Grid view</TooltipContent>
            </Tooltip>
          </div>

          {/* CC + Upload — separate rounded-full pills (same language as Filters / zoom) */}
          <div
            className="inline-flex items-center gap-2"
            role="group"
            aria-label="Upload and CompanyCam"
          >
            <LinkCompanyCamHeaderButton
              className={cn(
                SURFACE_ICON_BTN_CLS,
                "inline-flex items-center justify-center shadow-none",
              )}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="default"
                  className="h-9 gap-1.5 rounded-full px-4 font-normal"
                  onClick={onUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Upload className="size-3.5" aria-hidden />
                  )}
                  Upload
                </Button>
              </TooltipTrigger>
              <TooltipContent>Upload {uploadAcceptLabel}</TooltipContent>
            </Tooltip>
          </div>

          <input
            ref={uploadRef}
            type="file"
            className="hidden"
            multiple
            accept={accept}
            onChange={onUploadChange}
          />
        </div>
      </div>

      {/* Meta under search column */}
      <div className="flex max-w-md flex-wrap items-center gap-x-3 gap-y-1 px-1 text-sm font-normal text-muted-foreground">
        <span className="tabular-nums">
          Showing {totalCount.toLocaleString()} {totalCount === 1 ? "file" : "files"}
        </span>
        {companyCamProjectId && companyCamName ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={`https://app.companycam.com/projects/${companyCamProjectId}`}
                rel="noopener noreferrer"
                target="_blank"
                aria-label={
                  isImportingCompanyCam
                    ? `Importing from CompanyCam project ${companyCamName}`
                    : `Open CompanyCam project ${companyCamName}`
                }
                className="inline-flex max-w-[16rem] items-center gap-1.5 transition-colors hover:text-foreground"
              >
                <span className="shrink-0 tracking-wide">CC</span>
                <LinkIcon className="size-3 shrink-0 opacity-70" aria-hidden />
                <span className="min-w-0 truncate">{companyCamName}</span>
                {isImportingCompanyCam ? (
                  <Loader2
                    className="size-3.5 shrink-0 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                ) : (
                  <Check
                    className="size-3.5 shrink-0 text-success"
                    aria-hidden
                  />
                )}
                <span className="sr-only">
                  {isImportingCompanyCam ? "Importing…" : "Connected"}
                </span>
              </a>
            </TooltipTrigger>
            <TooltipContent>
              {isImportingCompanyCam
                ? "Importing…"
                : `Open CompanyCam project: ${companyCamName}`}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}
